import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalShell from '../components/PortalShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './PortalTickets.css';

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  pending: 'Awaiting Your Reply',
  resolved: 'Resolved',
  closed: 'Closed'
};

function PortalTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      setTickets(await apiClient.get('/api/tickets/mine'));
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const submitNewTicket = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      setFormError('Please enter a subject.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const data = await apiClient.post('/api/tickets/mine', { subject, description });
      setShowNewForm(false);
      setSubject('');
      setDescription('');
      navigate(`/portal/tickets/${data.ticket_id}`);
    } catch (error) {
      console.error('Error creating ticket:', error);
      setFormError('Something went wrong submitting your ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { key: 'ticket_number', label: '#', sortable: true, render: (row) => `#${row.ticket_number}` },
    { key: 'subject', label: 'Subject', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <span className={`portal-ticket-status portal-ticket-status-${row.status}`}>{STATUS_LABELS[row.status] || row.status}</span>
    },
    {
      key: 'created_at',
      label: 'Opened',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleDateString()
    }
  ];

  return (
    <PortalShell>
      <div className="portal-tickets-header">
        <h1>Support</h1>
        <Button onClick={() => setShowNewForm((v) => !v)}>{showNewForm ? 'Cancel' : 'New Ticket'}</Button>
      </div>

      {showNewForm && (
        <form className="portal-new-ticket" onSubmit={submitNewTicket}>
          <FormField id="subject" label="Subject" required>
            <input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What do you need help with?" />
          </FormField>
          <FormField id="description" label="Details">
            <textarea id="description" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Give us as much detail as you can." />
          </FormField>
          {formError && <div className="portal-form-error">{formError}</div>}
          <Button type="submit" disabled={submitting}>{submitting ? 'Submitting…' : 'Submit Ticket'}</Button>
        </form>
      )}

      {loading ? (
        <p>Loading your tickets…</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={tickets}
          rowKey="ticket_id"
          onRowClick={(row) => navigate(`/portal/tickets/${row.ticket_id}`)}
          emptyMessage="You haven't opened any support tickets yet."
        />
      )}
    </PortalShell>
  );
}

export default PortalTickets;
